import { Empty, LifeIdDrawer, ListItem, Loader } from '@components/ui';
import { useAppSelector } from '@hooks/app/redux';
import { actions } from '@store/ducks/life-id';
import { useVirtualizer } from '@tanstack/react-virtual';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { IDateRange, IVitalListEntry } from '../../../../types/life-id';
import TStore from '../../../../types/store';
import { TVitalHistoryMeta } from '../../../../types/vital-history';
import Row from './row';

interface Props {
  vitalListEntry: IVitalListEntry;
  range: IDateRange;
  parentRef: React.RefObject<HTMLDivElement>;
  headerHeight: number;
}

const HistoryList: React.FC<Props> = ({ range, vitalListEntry, parentRef, headerHeight }) => {
  const { userId } = useParams<{ userId: any }>();

  const meta = useSelector((store: TStore) => store.vitalHistory.meta);
  const data = useSelector((store: TStore) => store.lifeId.items);
  const pagination = useSelector((store: TStore) => store.lifeId.searchPagination);
  const providerId = useSelector((state: TStore) => state.users.business[0].id);
  const status = useAppSelector((state) => state.lifeId.getVitalDataItemsRequestStatus);
  const dispatch = useDispatch();

  const isFetchingNextPage = status === 'pending';

  useEffect(() => {
    dispatch(
      actions.getVitalDataItemsRequest({
        userId,
        range,
        urlPart: vitalListEntry.urlPart,
        providerId,
      }),
    );
  }, [dispatch, userId, vitalListEntry.urlPart, range, providerId]);

  useEffect(() => {
    return () => {
      dispatch(actions.resetVitalItems());
    };
  }, []);

  const loadMoreRows = useCallback(() => {
    dispatch(
      actions.getVitalDataItemsRequest({
        userId,
        range,
        urlPart: vitalListEntry.urlPart,
        increasePage: true,
        providerId,
      }),
    );
  }, [vitalListEntry, range, providerId, userId]);

  const hasNextPage = pagination.total !== data.length;

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? pagination.total + 1 : pagination.total,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 63,
    overscan: 5,
    scrollMargin: headerHeight,
    getItemKey: (idx) => data[idx]?.id ?? idx,
  });
  const items = rowVirtualizer.getVirtualItems();

  React.useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem) {
      return;
    }

    if (lastItem.index >= data.length - 1 && !isFetchingNextPage && hasNextPage) {
      loadMoreRows();
    }
  }, [loadMoreRows, data.length, isFetchingNextPage, items, hasNextPage]);

  if (data.length === 0 && isFetchingNextPage) {
    return null;
  }

  if (data.length === 0) {
    return <Empty title="Žiadne dáta" style={{ margin: 40 }} />;
  }

  return (
    <LifeIdDrawer.Scroller
      totalHeight={rowVirtualizer.getTotalSize()}
      startPoint={items[0].start - rowVirtualizer.options.scrollMargin}
    >
      <ListItem.Group>
        {items.map((virtualRow) => {
          const record = data[virtualRow.index];
          const isLoaderRow = virtualRow.index > data.length - 1;

          if (isLoaderRow) {
            return (
              <div
                className="load-more"
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
              >
                <Loader size="small" />
              </div>
            );
          }

          if (record) {
            return (
              <Row
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
                meta={meta as TVitalHistoryMeta}
                {...vitalListEntry.getDetailRowProps(record)}
              />
            );
          }

          return null;
        })}
      </ListItem.Group>
    </LifeIdDrawer.Scroller>
  );
};

export default React.memo(HistoryList);
